/**
 * VisitorNudge — soft register CTA for anonymous landing-page visitors.
 *
 * Motivation:
 *   Analytics show ~65% of first-time visitors leave without registering.
 *   Most of them scroll past the hero CTA, read the "How it works" section,
 *   and bail without an obvious next step. A small, dismissible nudge that
 *   appears after 15 seconds is a low-risk way to catch that fall-off.
 *
 * Behaviour:
 *   * Only mounts when the user is NOT authenticated (parent decides).
 *   * Appears after 15 seconds of scroll or dwell — long enough that the
 *     user has actually read something.
 *   * Dismissible for the current browser session. If they dismiss it, we
 *     don't re-show it — respecting their choice.
 *   * On click, opens the auth modal in "signup" mode.
 *
 * A11y: role=complementary, aria-labelled, keyboard-dismissible.
 */
import { useEffect, useState } from "react";
import { X, Sparkles, ArrowRight } from "lucide-react";

const DWELL_MS = 15_000;
const DISMISS_KEY = "dhruva_visitor_nudge_dismissed";

export default function VisitorNudge({ onRegister }) {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === "1") return;
    } catch (_) { /* privacy mode — proceed */ }
    const t = setTimeout(() => setVisible(true), DWELL_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible]);

  const dismiss = () => {
    setClosing(true);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch (_) { /* noop */ }
    setTimeout(() => setVisible(false), 240);
  };

  if (!visible) return null;

  return (
    <aside
      role="complementary"
      aria-label="Register free"
      className={`fixed z-40 left-3 right-3 sm:left-auto sm:right-6 sm:w-[380px] bottom-4 sm:bottom-6 rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden transition-all duration-300 ${closing ? "opacity-0 translate-y-3" : "opacity-100 translate-y-0"}`}
      data-testid="visitor-nudge"
    >
      <div className="relative p-4 pr-9 flex items-start gap-3">
        <span
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-600 via-fuchsia-500 to-rose-500"
        />
        <div className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-violet-100 to-rose-100 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-violet-700" />
        </div>
        <div className="min-w-0">
          <p className="text-[13.5px] font-bold text-slate-900 leading-tight">
            New here? Save 60 min of research.
          </p>
          <p className="mt-0.5 text-[11.5px] text-slate-600 leading-snug">
            Enter your CET score and we&apos;ll show your predicted colleges — free, no card needed.
          </p>
          <button
            type="button"
            onClick={() => { dismiss(); onRegister?.(); }}
            className="mt-2.5 inline-flex items-center h-8 px-3 rounded-md text-[12px] font-bold text-white bg-gradient-to-r from-violet-600 to-rose-500 hover:from-violet-500 hover:to-rose-400 shadow-sm hover:shadow-md transition-all"
            data-testid="visitor-nudge-register"
          >
            Register free <ArrowRight className="w-3 h-3 ml-1.5" />
          </button>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute top-2.5 right-2.5 text-slate-400 hover:text-slate-800 p-0.5 rounded"
          data-testid="visitor-nudge-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </aside>
  );
}
